// useUndoBar — the timer + lifecycle behind the R1 UndoBar (W39.1).
//
// Why a hook and not a bare setTimeout at each call site: there are five
// delete entry points, and the failure mode of the naive version is a leaked
// timer that fires after the component is gone and calls `setState` on a dead
// tree (or worse, calls a `restore` mutation the user never asked for). Every
// exit path here — timeout, undo, manual dismiss, unmount, a second delete
// replacing the first — goes through one `stop()`.
//
// The countdown itself keeps running under `prefers-reduced-motion: reduce`.
// That is deliberate and is the opposite of the usual rule: the reduced-motion
// contract is about *animation*, and the countdown is **time semantics** — the
// user needs to know how long their chance to undo lasts. Only the ring's
// rotation is suppressed (it is handled in UndoBar.tsx, not here).
//
// **There is deliberately no progress value here.** The ring is a pure-CSS
// `steps()` sweep (`globals.css` `undoBarCountdown`), so nothing in the app
// needs a number ticking down. This hook used to carry one — `progress`,
// written from a 100ms `setInterval` — and **no component ever read it**:
// `UndoBar` renders `animate-[undoBarCountdown_8s_linear_forwards]` and a
// constant `data-countdown="running"`. So every page hosting a bar
// (`DiaryDetail`, `CalendarEventDetail`, `Wishes`, `anniversaries/[id]`)
// re-rendered 40 times per delete, measured, with a value that went nowhere.
// The interval existed only to publish that value, so it is gone too and the
// expiry is a single `setTimeout` — which also makes the window close exactly
// on time rather than up to 100ms late.

import { useCallback, useEffect, useRef, useState } from "react";

/** 8s, not the toast queue's 2500ms — see `docs/spec/w39.1-R1-undo-bar.md`. */
export const UNDO_DURATION_MS = 8000;

/**
 * What the bar is offering to put back.
 *
 * Either one row (`id`) or several (`ids`). W40.1 F7 added the second form: a
 * non-contiguous multi-day selection stores N rows, so "undo the create" has to
 * take all N back — a bar that restored only the first would leave the user
 * with a silent half-reversal.
 *
 * A union rather than two optional fields on purpose. With
 * `{ id?: string; ids?: string[] }` every existing call site would still
 * compile, including the ones that would then be allowed to pass neither and
 * hand the hook an item it cannot restore — the exact state `Calendar.tsx`'s
 * comment says is "worse than a throw".
 */
export type UndoItem =
  | { id: string; ids?: never }
  | { ids: string[]; id?: never };

export interface UseUndoBarOptions<T extends UndoItem> {
  /** Called when the countdown expires. The delete stays deleted. */
  onExpire?: (item: T) => void;
  durationMs?: number;
}

export interface UseUndoBarResult<T extends UndoItem> {
  /** The pending item, or null when nothing is pending. */
  item: T | null;
  /** Show the bar for this item and start (or restart) the countdown. */
  begin: (item: T) => void;
  /** Clear the bar without running `onExpire` (the user hit 復原). */
  clear: () => void;
}

export function useUndoBar<T extends UndoItem>({
  onExpire,
  durationMs = UNDO_DURATION_MS,
}: UseUndoBarOptions<T> = {}): UseUndoBarResult<T> {
  const [item, setItem] = useState<T | null>(null);

  // Kept in a ref so the timeout never has to be re-created just because the
  // callback identity changed — a restarted timer is a restarted countdown,
  // which would silently extend the undo window.
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clear = useCallback(() => {
    stop();
    setItem(null);
  }, [stop]);

  const begin = useCallback(
    (next: T) => {
      // A second delete while one is pending replaces it — one bar at a time,
      // never a queue. The replaced item is NOT expired: the server already
      // holds the soft delete, and the 7-day trash is the real safety net.
      stop();
      setItem(next);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setItem(null);
        onExpireRef.current?.(next);
      }, durationMs);
    },
    [durationMs, stop],
  );

  // Unmount is one of the exit paths the naive version forgets.
  useEffect(() => stop, [stop]);

  return { item, begin, clear };
}